/** Libraries */
import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Form, Button, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

/**  Constant, Services */
import { PencilSquare, Trash } from 'react-bootstrap-icons';
import { UserData, UserConstants, DeleteResponse } from "./user.types";
import { AppDispatch, RootState } from "../../store/store";
import { deleteUser, fetchUsers, bulkDeleteUser, updateUser } from './userSlice';
import { useLoader } from "../../context/LoaderContext";
import ConfirmationModal from '../../components/Confirmation-Modal/Confirmation-Modal';
import UserEditableRow from "../User/UserEditableRow";
import SearchInput from "../../components/SearchInput/SearchInput";

const UserList: React.FC = () => {
    const userState = useSelector((state: RootState) => state.users);
    const siteMetaData = useSelector((state: RootState) => state.users.sites);
    const dispatch = useDispatch<AppDispatch>();
    const { setLoading } = useLoader();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectedUsersForDelete, setSelectedUsersForDelete] = useState<UserData[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [selectedUsers, setSelectedUser] = useState<UserData[]>([]);
    const [shouldDisplayConfirmModal, setConfirmModalVisibility] = useState<Boolean>(false);
    const [editRowId, setEditRowId] = useState<string | null>(null);
    const loginUserData = useSelector((state: RootState) => state.users.loginUser);
    const userListData = userState.users;
    const [editedUserFormData, setEdittedUserFormData] = useState<UserData>({
        id: "",
        firstName: '',
        lastName: '',
        email: '',
        siteName: [],
        status: '',
        preferredLanguage: ''
    })

    /**
     * Get site label from Id
     * @param ids 
     * @returns 
     */
    const getSiteLabels = (ids: string[]) => {
        try {
            if (!ids) return "";
            return ids
                .map((id) => siteMetaData.find((opt) => opt.id === id)?.label)
                .filter(Boolean)
                .join(", ");
        } catch (exe) {
            console.log(exe);
            return "";
        }
    };

    /**
      * Filter users based on the search term by matching First Name, Last Name, Email, and Sites.
      * @returns 
      */
    const filteredUserListData = useMemo<UserData[]>(() => {
        try {
            const search = searchValue.toLowerCase();
            const filtered = userListData
                .filter((row) => {
                    const siteLabels = getSiteLabels(row.siteName).toLowerCase();
                    return (
                        row.firstName?.toLowerCase().includes(search) ||
                        row.lastName?.toLowerCase().includes(search) ||
                        row.email?.toLowerCase().includes(search) ||
                        siteLabels.toLowerCase().includes(search)
                    )
                });
            return filtered;
        } catch (exe) {
            console.error("Error while filtering users:", exe);
            return [];
        }
    }, [searchValue, userListData]);

    const totalPages = Math.ceil(filteredUserListData.length / UserConstants.DATA_PER_PAGE);
    const startIdx = (currentPage - 1) * UserConstants.DATA_PER_PAGE;
    const endIdx = Math.min(startIdx + UserConstants.DATA_PER_PAGE, filteredUserListData.length);
    const userListPageData = filteredUserListData.slice(startIdx, endIdx);

    useEffect(() => {
        // fetchUser();
    }, []);

    useEffect(() => {
        // If page becomes empty after delete and we're not on the first page
        if (userListPageData.length === 0 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }, [userListPageData, currentPage]);

    /**
     *  Fetch account users
     */
    const fetchUser = async () => {
        try {
            setLoading(true);
            const result = await dispatch(fetchUsers(loginUserData)).unwrap();
            setLoading(false);
        } catch (error: any) {
            console.log(error);
            setLoading(false);
            const errorMessage = error?.response?.data?.message || UserConstants.ERROR_FETCH_USER;
            toast.error(errorMessage);
        }
    };

    /**
     * Is all user selected
     * @returns 
     */
    const allUserSelected = () => {
        try {
            if (selectedUsers.length > 0 && userListPageData.length > 0) {
                return (selectedUsers.length === userListPageData.length) && selectedUsers.every(item => userListPageData.includes(item));
            } else {
                return false;
            }
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * Toggle selected users
     */
    const toggleSelectAllUser = () => {
        try {
            setSelectedUser(allUserSelected() ? [] : userListPageData.map(item => item));
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * Toggle single user selection
     * @param userData 
     */
    const toggleSingleUserSelection = (userData: UserData) => {
        try {
            setSelectedUser(prevUsers => {
                const exists = prevUsers.some(u => u.id === userData.id);
                if (exists) {
                    // Remove if already in list
                    return prevUsers.filter(u => u.id !== userData.id);
                } else {
                    // Add if not in list
                    return [...prevUsers, userData];
                }
            });
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * Is user selected from checkbox
     * @param user 
     * @returns 
     */
    const isUserSelected = (user: UserData) => selectedUsers.some(selected => selected.id === user.id);

    /**
     * Pagination-  Go to page
     * @param page 
     */
    const goToPage = (page: number) => {
        try {
            setCurrentPage(page);
        } catch (exe) {
            console.log(exe);
        }
    }

    /**
     * Confirm delete action
     * @param userData 
     */
    const confirmDelete = (userData: UserData) => {
        try {
            setConfirmModalVisibility(true);
            setSelectedUsersForDelete(prev => [...prev, userData]);
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * Remove all user confirmations
     */
    const confirmRemoveUserDelete = () => {
        try {
            setConfirmModalVisibility(true);
            setSelectedUsersForDelete(selectedUsers);
        } catch (exe) {
            console.log(exe);
        }
    }

    /**
     * Close delete confirm modal
     */
    const handleCloseModal = () => {
        try {
            setConfirmModalVisibility(false);
            setSelectedUsersForDelete([]);
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * On confirmation prompt hide
     */
    const onConfirmModalHide = () => {
        setConfirmModalVisibility(false);
        setSelectedUsersForDelete([]);
    }

    /**
     * handle single delete
     */
    const handleSingleDelete = async () => {
        try {
            if (selectedUsersForDelete) {
                setLoading(true);
                const result = await dispatch(deleteUser(selectedUsersForDelete)).unwrap();
                setLoading(false);
                if (result) {
                    toast.success(UserConstants.USER_DELETED);
                    setSelectedUsersForDelete([]);
                    setConfirmModalVisibility(false);
                    setSelectedUser([]);
                } else {
                    toast.error(UserConstants.ERROR_DELETE_USER);
                }
            }
        } catch (error: any) {
            setLoading(false);
            setConfirmModalVisibility(false);
            let errorMessage = UserConstants.ERROR_DELETE_USER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    }

    /**
     * handle build delete
     */
    const handleBulkDelete = async () => {
        try {
            if (selectedUsersForDelete) {
                setLoading(true);
                const result = await dispatch(bulkDeleteUser(selectedUsersForDelete)).unwrap();
                setLoading(false);

                const failedUsers = result
                    .filter((r: DeleteResponse) => !r.success)
                    .map((r: DeleteResponse) => {
                        const matchedUser = selectedUsersForDelete.find((u) => u.id === r.userId);
                        return matchedUser ? matchedUser.firstName : r.userId; // fallback to id if not found
                    });

                setSelectedUsersForDelete([]);
                setConfirmModalVisibility(false);
                setSelectedUser([]);

                if (failedUsers.length > 0) {
                    const msg = `Failed to delete user(s): ${failedUsers.join(", ")}`;
                    toast.error(msg);
                } else {
                    toast.success(UserConstants.USER_DELETED);
                }
            }
        } catch (error: any) {
            setLoading(false);
            setConfirmModalVisibility(false);
            let errorMessage = UserConstants.ERROR_DELETE_USER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    }

    /**
     * After confirm delete action
     */
    const handleDelete = async () => {
        try {
            if (selectedUsersForDelete) {
                if (selectedUsersForDelete.length == 1) {
                    handleSingleDelete();
                } else {
                    handleBulkDelete();
                }
            }
        } catch (error: any) {
            setLoading(false);
            setConfirmModalVisibility(false);
            let errorMessage = UserConstants.ERROR_DELETE_USER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    };

    /**
     * Delete confirmation message
     * @returns 
     */
    const deleteConfirmationMessage = () => {
        try {
            const firstNames = selectedUsersForDelete.map(user => user.firstName).join(', ');
            if (firstNames.length === 0) return '';
            const plural = firstNames.length > 1;
            return `Are you sure you want to delete "${firstNames}" ${plural ? 'Users' : 'User'}?`;
        } catch (exe) {
            console.log(exe);
            return "";
        }
    }

    /**
     * On click on edit
     * @param user 
     */
    const handleEdit = (user: UserData) => {
        setEditRowId(user.id);
        setEdittedUserFormData(user);
    };

    /**
     * On filed change
     * @param field 
     * @param value 
     */
    const handleChange = (field: keyof UserData, value: string) => {
        setEdittedUserFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    /**
     * On click on Save button
     */
    const handleSave = async () => {
        try {
            if (editRowId !== null) {
                setLoading(true);
                const result = await dispatch(updateUser({ user: editedUserFormData, loginUserData })).unwrap();
                setLoading(false);
                if (result) {
                    toast.success(UserConstants.USER_UPDATED);
                    setEditRowId(null);
                    setEdittedUserFormData({
                        id: "",
                        firstName: '',
                        lastName: '',
                        email: '',
                        siteName: [],
                        status: '',
                        preferredLanguage: ''
                    });
                } else {
                    toast.error(UserConstants.ERROR_UPDATE_USER);
                }
            }
        } catch (error: any) {
            setLoading(false);
            let errorMessage = UserConstants.ERROR_UPDATE_USER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    };

    /**
     * On click on cancel button
     */
    const handleCancel = () => {
        setEditRowId(null);
        setEdittedUserFormData({
            id: "", firstName: '',
            lastName: '',
            email: '',
            siteName: [],
            status: '',
            preferredLanguage: ''
        });
    };

    return (
        <>
            <div className="bg-white p-4 rounded shadow-sm">
                <h6 className="mb-3 fs-18">Manage User</h6>
                <Row className="">
                    <Col xl={9} className="mb-lg-3 mb-xl-0">
                        <div className="ps-3 d-flex align-items-center btn-remove-all-user-container">
                            <Form.Check id="remove-users" checked={allUserSelected()} onChange={toggleSelectAllUser} />
                            <button type="button" className="ms-2 mb-0 d-flex align-items-center btn btn-danger btn-remove-all-users rounded-pill" onClick={() => confirmRemoveUserDelete()}>
                                Remove user(s)
                            </button>
                        </div>
                    </Col>
                    <Col xl={3}>
                        <div className="d-flex flex-column flex-md-row justify-content-md-end justify-content-start gap-3">
                            <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search user" />
                        </div>
                    </Col>
                </Row>
                <div className="table-responsive mt-3 min-h-250">
                    <Table hover className="text-center align-middle">
                        <thead>
                            <tr>
                                <th className="request-queue-table-header">

                                </th>
                                {UserConstants.USER_LIST_HEADER.map((header, index) => (
                                    <th key={index} className="request-queue-table-header">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {userListPageData.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: "center" }}>
                                        No Data Found
                                    </td>
                                </tr>
                            ) : (
                                userListPageData.map((user: UserData, index: number) => (
                                    editRowId === user.id ? (
                                        <UserEditableRow
                                            key={user.id}
                                            siteData={siteMetaData}
                                            user={editedUserFormData as UserData}
                                            onChange={handleChange}
                                            onSave={handleSave}
                                            onCancel={handleCancel}
                                            checked={isUserSelected(user)}
                                            onCheckboxChange={toggleSingleUserSelection}
                                        />
                                    ) : (
                                        <tr key={index}>
                                            <td>
                                                <Form.Check checked={isUserSelected(user)} onChange={() => toggleSingleUserSelection(user)} />
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                                {user.firstName}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                                {user.lastName}
                                            </td>
                                            <td style={{ fontSize: "12px" }}>
                                                {user.email}
                                            </td>
                                            <td style={{ fontSize: "12px", width: "30%" }}>
                                                {getSiteLabels(user.siteName)}
                                            </td>
                                            { /* <td style={{ fontSize: "12px", color: "#353535" }}>
                                            {item.status}
                                        </td> */ }
                                            <td style={{ color: "#353535" }}>
                                                <PencilSquare className="me-3 action-icon-background-color fs-20" title="Edit" role="button" onClick={() => handleEdit(user)} />
                                                <Trash className="text-danger fs-20" role="button" title="Delete" onClick={() => confirmDelete(user)} />
                                            </td>
                                        </tr>
                                    )
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
                {filteredUserListData.length > UserConstants.DATA_PER_PAGE && (
                    <div className="d-flex align-items-center justify-content-center">
                        <p className="mb-0 text-secondary">
                            Showing {startIdx + 1} to {endIdx} of {filteredUserListData.length}{" "}
                            entries
                        </p>
                        <div className="ms-3">
                            {Array.from({ length: totalPages }, (_, index) => (
                                <button type="button"
                                    key={index}
                                    onClick={() => goToPage(index + 1)}
                                    className={`btn btn-sm rounded-circle me-1 pagination-button ${currentPage === index + 1
                                        ? "paginationButton text-white"
                                        : "btn-outline-secondary"
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <ConfirmationModal
                    show={shouldDisplayConfirmModal && selectedUsersForDelete.length > 0}
                    title={UserConstants.DELETE_CONFIRMATION_TITLE}
                    message={deleteConfirmationMessage()}
                    onConfirm={handleDelete}
                    onCancel={handleCloseModal}
                    onHide={onConfirmModalHide}
                    cancelBtnText="Cancel"
                    confirmBtnText="Remove"
                    confirmVariant="danger"
                />
            </div>
        </>
    );
};

export default UserList;